import { useEffect, useRef } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Settings,
  Package,
  Bell,
  ShieldAlert,
  Users,
  Store,
  CreditCard,
  Filter,
  RefreshCw,
  type LucideIcon,
} from 'lucide-react';
import { cn } from 'src/lib/utils';
import { Separator } from 'src/components/ui/separator';

interface NavItem {
  label: string;
  path: string;
  icon: LucideIcon;
  tab?: string;
}

interface NavGroup {
  label: string;
  items: NavItem[];
}

const NAV_GROUPS: NavGroup[] = [
  {
    label: 'Company',
    items: [
      { label: 'General', path: '/settings/general', icon: Settings, tab: 'general' },
      { label: 'Inventory Planning', path: '/settings/general', icon: Package, tab: 'inventory' },
      { label: 'Alerts', path: '/settings/general', icon: Bell, tab: 'alerts' },
      {
        label: 'Issue Detection',
        path: '/settings/general',
        icon: ShieldAlert,
        tab: 'issue-detection',
      },
      {
        label: 'Order Filtering',
        path: '/settings/general',
        icon: Filter,
        tab: 'order-filtering',
      },
      {
        label: 'Integrations',
        path: '/settings/general',
        icon: RefreshCw,
        tab: 'integrations',
      },
    ],
  },
  {
    label: 'Team',
    items: [
      { label: 'Users', path: '/settings/users', icon: Users },
      { label: 'Multi-Store', path: '/settings/multi-store', icon: Store },
    ],
  },
  {
    label: 'Billing',
    items: [{ label: 'Plans & Billing', path: '/settings/billing', icon: CreditCard }],
  },
];

function useActiveItem() {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const currentTab = searchParams.get('tab');

  return (item: NavItem) => {
    const pathMatch = location.pathname === item.path;
    if (item.tab) {
      return pathMatch && (currentTab === item.tab || (!currentTab && item.tab === 'general'));
    }
    return pathMatch;
  };
}

function DesktopNav() {
  const navigate = useNavigate();
  const isActive = useActiveItem();

  return (
    <nav className="space-y-6">
      {NAV_GROUPS.map((group, i) => (
        <div key={group.label}>
          {i > 0 && <Separator className="mb-4" />}
          <h3 className="mb-2 px-3 text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">
            {group.label}
          </h3>
          <div className="space-y-0.5">
            {group.items.map((item) => {
              const Icon = item.icon;
              const active = isActive(item);
              return (
                <button
                  key={`${item.label}`}
                  type="button"
                  onClick={() => navigate(item.tab ? `${item.path}?tab=${item.tab}` : item.path)}
                  className={cn(
                    'flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-[13px] font-medium transition-colors',
                    active
                      ? 'bg-foreground/[0.06] text-foreground'
                      : 'text-muted-foreground hover:bg-foreground/[0.04] hover:text-foreground'
                  )}
                >
                  <Icon className={cn('h-4 w-4', active && 'text-secondary')} />
                  {item.label}
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </nav>
  );
}

function MobileNav() {
  const navigate = useNavigate();
  const isActive = useActiveItem();
  const allItems = NAV_GROUPS.flatMap((g) => g.items);
  const activePillRef = useRef<HTMLButtonElement>(null);
  const activeLabel = allItems.find(isActive)?.label;

  // The pill row scrolls horizontally, so a tab late in the row opens off-screen.
  useEffect(() => {
    activePillRef.current?.scrollIntoView({ inline: 'center', block: 'nearest' });
  }, [activeLabel]);

  return (
    <div className="flex gap-1 pb-1">
      {allItems.map((item) => {
        const Icon = item.icon;
        const active = isActive(item);
        return (
          <button
            key={`m-${item.label}`}
            ref={active ? activePillRef : undefined}
            type="button"
            onClick={() => navigate(item.tab ? `${item.path}?tab=${item.tab}` : item.path)}
            className={cn(
              'flex shrink-0 items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium transition-colors',
              active
                ? 'bg-foreground text-background'
                : 'bg-muted text-muted-foreground hover:text-foreground'
            )}
          >
            <Icon className="h-3 w-3" />
            {item.label}
          </button>
        );
      })}
    </div>
  );
}

interface SettingsLayoutProps {
  children: React.ReactNode;
}

export default function SettingsLayout({ children }: SettingsLayoutProps) {
  return (
    <div className="mx-auto w-full max-w-4xl">
      <div className="mb-8">
        <h1 className="text-2xl font-semibold tracking-tight">Settings</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Manage your company preferences, team, and integrations.
        </p>
      </div>

      {/* Mobile horizontal nav */}
      <div className="-mx-4 mb-6 overflow-x-auto px-4 scrollbar-hide md:hidden">
        <MobileNav />
      </div>

      {/* Desktop layout */}
      <div className="flex gap-10">
        <aside className="hidden w-52 shrink-0 md:block">
          <div className="sticky top-6">
            <DesktopNav />
          </div>
        </aside>
        <main className="min-w-0 flex-1">{children}</main>
      </div>
    </div>
  );
}
